import * as React from 'react'
import { Link } from 'react-router-dom'
import { useTable, usePagination, Column } from 'react-table'
import {
  OrganizationTeamMemberDisplayStatuses,
  OrganizationTeamMemberStatus
} from '../../constants/organization-team-member-statuses'
import { IStationUserFilters, IStationUserSearchObject } from '../../types'
import ArchiveIcon from '../icons/ui/Archive'
import ImageView from '../ui/ImageView'
import ContextMenu from '../ui/ContextMenu'
import PreloaderCircle from '../ui/PreloaderCircle'
import UIMessages from 'src/utils/ui-messages'
import Edit from '../icons/drivers/Edit'

export interface IStationUserListProps {
  filters: IStationUserFilters
  stationUser: IStationUserSearchObject[]
  total: number
  isFetching: boolean
  type: string
  statuses: OrganizationTeamMemberStatus[]
  fetchOrgList: () => void
  fetchStationUserList: (filters: IStationUserFilters) => Promise<any>
  archiveStationUser: (stationUser: IStationUserSearchObject) => Promise<any>
  currentOrgId: string
}

interface ITeamMemberTableState {
  pages: number
  pageSize: number
  isSaving: boolean
}

interface IProps<T extends object> {
  columns: Array<Column<T>>
  data: T[]
  loading: boolean
  pageCount: number
  filters: IStationUserFilters
  fetchData: (filters: IStationUserFilters, params: { pageIndex: number; pageSize: number }) => void
}

const PAGE_SIZE = 10

function Table<T extends object>({ columns, data, fetchData, loading, pageCount: controlledPageCount }: IProps<T>) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setPageSize,
    filters,
    state: { pageIndex, pageSize }
  } = useTable<T>(
    {
      columns,
      data,
      initialState: { pageIndex: 0 },
      manualPagination: true,
      pageCount: controlledPageCount
    },
    usePagination
  )

  React.useEffect(() => {
    fetchData(filters, { pageIndex, pageSize })
  }, [filters, pageIndex, pageSize])
  //Declaration of URL's
  const getLink = (): string => {
    let link = ''
    if (process.env.REACT_APP_GATEWAY_URL) {
      link = process.env.REACT_APP_GATEWAY_URL
      if (process.env.REACT_APP_GATEWAY_URL.includes('dev')) {
        const lnk = process.env.REACT_APP_GATEWAY_URL.split(':')
        link = lnk[1]
      } else if (process.env.REACT_APP_GATEWAY_URL.includes('radar')) {
        const lnk1 = process.env.REACT_APP_GATEWAY_URL.split(':')
        link = lnk1[1]
      }
    }
    return link
  }
  //Declaration of URL's
  return (
    <React.Fragment>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th {...column.getHeaderProps()}>{column.render('Header')}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {loading ? (
            <tr>
              <td colSpan={5}>
                <div className='flex justify-center col-12'>
                  <PreloaderCircle loading={loading} />
                </div>
              </td>
            </tr>
          ) : (
            <>
              {data.length ? (
                rows.map(
                  (row, i) =>
                    +prepareRow(row) || (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell, index) => {
                          return (
                            <td {...cell.getCellProps()} key={`stationUserTableColumn-${index}`}>
                              {cell.render('Cell')}
                            </td>
                          )
                        })}
                      </tr>
                    )
                )
              ) : (
                <tr>
                  <td colSpan={5}>
                    <div className='flex items-center justify-center p2'>
                      <img width={130} src='/images/no_data.gif' alt='No data availabe.' />
                    </div>
                  </td>
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>
      {/* <ReactTablePagination
        page={pageIndex}
        pages={controlledPageCount}
        pageSize={pageSize}
        canNext={canNextPage}
        canPrevious={canPreviousPage}
        nextPage={nextPage}
        previousPage={previousPage}
      /> */}
      {/* Footer */}
      <div
        className='footer-copyright border-box text-left py-3 font-black h6 bold '
        style={{ marginBottom: '5rem', marginTop: '2rem' }}
      >
        &copy; Copyright {new Date().getFullYear()}{' '}
        <a href={`${getLink()}`}>
          <u>GOIN'</u>
        </a>{' '}
        All rights reserved.
      </div>
    </React.Fragment>
  )
}

const CellBasicInfo = (props: { name: string; id: string }) => (
  <div className='flex items-center'>
    <ImageView entityId={props.id} isRounded={true} />
    <p className='ml1 TeamMemberName font-dark-grey'>{props.name}</p>
  </div>
)

const ActiveTeamMemberMenu = (props: {
  refreshData: () => Promise<any>
  stationUser: IStationUserSearchObject
  archive: (stationUser: IStationUserSearchObject) => Promise<any>
}) => {
  const menuCtx = React.useRef<any>()
  const { stationUser, archive, refreshData } = props
  const {
    common: { edit },
    teamMember: { moveToArchive, restoreFromArchive }
  } = UIMessages
  const onArchiveClickClick = async () => {
    await archive(stationUser)
    setTimeout(refreshData, 1000)
    menuCtx.current.mouseLeave()
  }
  return (
    <ContextMenu ref={menuCtx} style={{ zDepth: -1 }}>
      <div className='MenuItem cursor--pointer p1'>
        <Link className='flex items-center' to={`/station-user/edit/${stationUser.id}`}>
          <Edit className='icon--small mr1 svg-stroke-light-grey' />
          <div>{edit}</div>
        </Link>
      </div>
      <div className='MenuItem cursor--pointer p1'>
        <div className='flex items-center' onClick={onArchiveClickClick}>
          <ArchiveIcon className='icon--small mr1 svg-stroke-light-grey' />
          <div>{stationUser.isArchived ? restoreFromArchive : moveToArchive}</div>
        </div>
      </div>
    </ContextMenu>
  )
}

export default function StationUserTablePane(props: IStationUserListProps) {
  const [d, setData] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [isSaving, setIsSaving] = React.useState(false)
  const [pageCount, setPageCount] = React.useState(0)
  const { type } = props
  const total = props.total
  const columns = React.useMemo(
    () => [
      {
        Header: 'name',
        accessor: 'name',
        Cell: (data: any) => {
          const { id, userId, name } = data.row.original
          return <Link to={`/station-user/${id}`}>{name}</Link>
        }
      },
      {
        Header: 'Phone number',
        accessor: ({ phone }: any) => {
          return <>{phone ? phone : 'NA'}</>
        },
        className: 'flex items-center'
      },
      {
        Header: 'Email',
        accessor: 'email',
        className: 'flex items-center'
      },
      {
        Header: 'Status',
        id: 'status',
        accessor: ({ status, invitationTime }: any) => {
          invitationTime = new Date(invitationTime)

          if (new Date().getTime() > invitationTime.getTime()) {
            status = 'INVITATION_EXPIRED'
          }
          return OrganizationTeamMemberDisplayStatuses[status]
        },
        className: 'flex items-center',
        show: type !== 'archived'
      },
      {
        Header: 'Organization',
        accessor: 'organization',
        Cell: (data: any) => {
          const { organizationName } = data.row.original
          return organizationName
        }
      },
      {
        id: 'archive-menu',
        show: type !== 'archived',
        maxWidth: 40,
        className: 'overflow--visible',
        Cell: (data: any) => {
          const refresh = () => fetchStationUserData(props.filters)
          return (
            <ActiveTeamMemberMenu
              archive={props.archiveStationUser}
              stationUser={data.row.original}
              refreshData={refresh}
            />
          )
        }
      }
    ],
    [props.filters]
  )

  React.useEffect(() => {
    props.fetchOrgList()
  }, [])

  React.useEffect(() => {
    fetchStationUserData(props.filters)
  }, [props.filters])

  const fetchStationUserData = React.useCallback(async (filters: IStationUserFilters) => {
    if (!filters || !filters.orgId) {
      return
    }
    const total = await props.fetchStationUserList(filters)
    setPageCount(Math.ceil(total / PAGE_SIZE))
  }, [props.filters])

  return (
    <div className='react-table'>
      <Table
        columns={columns}
        data={props.stationUser}
        filters={props.filters}
        fetchData={fetchStationUserData}
        loading={props.isFetching}
        pageCount={pageCount}
      />
    </div>
  )
}



// WEBPACK FOOTER //
// ./src/components/station-user/StationUserTable.tsx